import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Newspaper, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { blogAPI } from '../services/api';

/**
 * Humanistix landing page — "AI for HR, with humans at the centre".
 * Three-pillar positioning: AI for HR / Controlled build-up / Change & adoption.
 * NL/EN toggle handled via the site-wide LanguageContext (no inline JS).
 */
const HumanistixLanding = () => {
  const { language } = useLanguage();
  const nl = language === 'nl';
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    blogAPI.getPosts()
      .then((posts) => setLatestPosts((posts || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans">
      <Header />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-blue-100/40 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 backdrop-blur border border-blue-200 text-xs font-semibold text-blue-700 mb-6"
              data-testid="landing-hero-badge"
            >
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              {nl ? 'AI for HR — met de mens centraal' : 'AI for HR — humans at the centre'}
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] mb-6">
              {nl ? (
                <>AI voor HR<br />die <span className="text-blue-500">écht</span><br />gebruikt wordt</>
              ) : (
                <>AI for HR<br />that people<br /><span className="text-blue-500">actually use</span></>
              )}
            </h1>

            <p className="text-base sm:text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
              {nl ? (
                <>Wij bouwen <strong>AI-oplossingen voor concrete businessproblemen</strong> in HR. Klein en gecontroleerd, samen met je HR-medewerkers — met evenveel aandacht voor change en adoptie als voor technologie.</>
              ) : (
                <>We build <strong>AI solutions for real business problems</strong> in HR. Small and controlled, together with your HR people — with as much focus on change and adoption as on technology.</>
              )}
            </p>

            {/* CTAs — pill cards */}
            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link
                to="/quickscan/start"
                className="group flex items-center gap-3 px-5 py-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/25 transition-all"
                data-testid="landing-cta-quickscan"
              >
                <span className="text-2xl">⚡</span>
                <span className="flex-1 text-left">
                  <b className="block text-sm">{nl ? 'Start de gratis quickscan' : 'Start the free quickscan'}</b>
                  <span className="block text-xs text-white/85">{nl ? 'Hoe AI-ready is je HR-team? • 4 min' : 'How AI-ready is your HR team? • 4 min'}</span>
                </span>
                <span className="text-xl group-hover:translate-x-1 transition-transform">›</span>
              </Link>
              <a
                href="#what"
                className="group flex items-center gap-3 px-5 py-4 rounded-xl bg-white border border-blue-200 hover:border-blue-400 text-slate-800 shadow-sm transition-all"
                data-testid="landing-cta-discover"
              >
                <span className="text-2xl">💬</span>
                <span className="flex-1 text-left">
                  <b className="block text-sm">{nl ? 'Ontdek wat we doen' : 'Discover what we do'}</b>
                  <span className="block text-xs text-slate-500">{nl ? 'Onze drie pijlers' : 'Our three pillars'}</span>
                </span>
                <span className="text-xl text-blue-500 group-hover:translate-x-1 transition-transform">›</span>
              </a>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-6 pt-6 border-t border-blue-100">
              {[
                { b: '245+', l: nl ? 'HR-teams gebenchmarkt' : 'HR teams benchmarked' },
                { b: '4 min', l: nl ? 'gratis quickscan' : 'free quickscan' },
                { b: '100%', l: nl ? 'mensgericht' : 'human-centred' },
              ].map((s) => (
                <div key={s.b} className="flex flex-col">
                  <b className="text-2xl font-extrabold text-slate-900">{s.b}</b>
                  <span className="text-xs text-slate-500">{s.l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Split-face visual */}
          <div className="relative bg-white/60 backdrop-blur rounded-3xl p-6 shadow-2xl shadow-blue-500/10 border border-blue-100" data-testid="landing-hero-svg">
            <svg viewBox="0 0 520 480" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" role="img" aria-label={nl ? 'Half mens, half AI-gezicht in Humanistix-kleuren' : 'Half human, half AI face in Humanistix colours'}>
              <defs>
                <linearGradient id="bgGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#eff6ff" /><stop offset=".55" stopColor="#dbeafe" /><stop offset="1" stopColor="#f3e8ff" />
                </linearGradient>
                <linearGradient id="skin" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#bfdbfe" /><stop offset="1" stopColor="#60a5fa" />
                </linearGradient>
                <linearGradient id="tech" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#0f172a" /><stop offset=".6" stopColor="#172554" /><stop offset="1" stopColor="#581c87" />
                </linearGradient>
                <linearGradient id="seam" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#2563eb" stopOpacity="0" /><stop offset=".5" stopColor="#2563eb" /><stop offset="1" stopColor="#a855f7" stopOpacity="0" />
                </linearGradient>
                <radialGradient id="eyeGlow" cx=".5" cy=".5" r=".5">
                  <stop offset="0" stopColor="#eff6ff" /><stop offset=".35" stopColor="#3b82f6" /><stop offset="1" stopColor="#2563eb" stopOpacity="0" />
                </radialGradient>
                <clipPath id="leftClip"><rect x="0" y="0" width="260" height="480" /></clipPath>
                <clipPath id="rightClip"><rect x="260" y="0" width="260" height="480" /></clipPath>
              </defs>

              <rect width="520" height="480" fill="url(#bgGrad)" />
              <circle cx="90" cy="90" r="70" fill="#2563eb" opacity=".07" />
              <circle cx="450" cy="400" r="90" fill="#7c3aed" opacity=".07" />
              <circle cx="460" cy="80" r="46" fill="#2563eb" opacity=".1" />

              {/* Left = human */}
              <g clipPath="url(#leftClip)">
                <path d="M260 60c-72 0-124 52-124 124 0 34 8 58-10 88-6 10-2 18 8 20 12 3 16 6 15 18-1 10 2 15 10 17 7 2 9 6 8 14-2 12 4 20 17 20h76V60z" fill="url(#skin)" />
                <path d="M260 42c-84 0-140 56-140 138 0 12 1 22 3 32 8-6 12-16 13-30 26-6 42-24 50-48 20 22 48 32 74 34V42z" fill="#1e3a8a" />
                <path d="M168 190c14-10 34-12 50-6" stroke="#172554" strokeWidth="7" strokeLinecap="round" fill="none" />
                <ellipse cx="196" cy="216" rx="17" ry="10" fill="#fff" />
                <circle cx="198" cy="216" r="7" fill="#172554" />
                <circle cx="200" cy="214" r="2.4" fill="#fff" />
                <path d="M252 230c-6 18-14 30-10 38 3 6 10 8 18 6" stroke="#60a5fa" strokeWidth="5" strokeLinecap="round" fill="none" opacity=".8" />
                <path d="M212 316c14 10 34 10 48 2" stroke="#1e40af" strokeWidth="8" strokeLinecap="round" fill="none" />
                <ellipse cx="172" cy="262" rx="16" ry="10" fill="#3b82f6" opacity=".35" />
              </g>

              {/* Right = AI */}
              <g clipPath="url(#rightClip)">
                <path d="M260 60c72 0 124 52 124 124 0 34-8 58 10 88 6 10 2 18-8 20-12 3-16 6-15 18 1 10-2 15-10 17-7 2-9 6-8 14 2 12-4 20-17 20h-76V60z" fill="url(#tech)" />
                <g stroke="#3b82f6" strokeWidth="2.5" fill="none" opacity=".9">
                  <path d="M280 100v46h34v26" />
                  <path d="M330 92v38h26v52" />
                  <path d="M280 340h44v-28" />
                  <path d="M300 380v-24h40" />
                  <path d="M356 300h26v-40" />
                  <path d="M280 250h22" />
                </g>
                <g fill="#60a5fa">
                  <circle cx="314" cy="172" r="4" /><circle cx="356" cy="182" r="4" />
                  <circle cx="324" cy="312" r="4" /><circle cx="340" cy="356" r="4" />
                  <circle cx="382" cy="260" r="4" /><circle cx="302" cy="250" r="4" />
                </g>
                <g stroke="#a855f7" strokeWidth="2" fill="none" opacity=".8">
                  <path d="M290 130h40v-20" />
                  <path d="M370 220v34h-26" />
                  <path d="M310 296h36" />
                </g>
                <g fill="#c4b5fd"><circle cx="330" cy="110" r="3.4" /><circle cx="344" cy="254" r="3.4" /><circle cx="346" cy="296" r="3.4" /></g>
                <g stroke="#3b82f6" strokeWidth="1.6" fill="#172554" opacity=".65">
                  <path d="M368 130l12 7v14l-12 7-12-7v-14z" />
                  <path d="M392 328l10 6v12l-10 6-10-6v-12z" />
                </g>
                <circle cx="322" cy="216" r="30" fill="url(#eyeGlow)" />
                <circle cx="322" cy="216" r="16" fill="#0f172a" stroke="#2563eb" strokeWidth="3" />
                <circle cx="322" cy="216" r="7" fill="#2563eb" />
                <circle cx="322" cy="216" r="22" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="5 7" />
                <path d="M300 186c16-8 36-8 50 0" stroke="#2563eb" strokeWidth="6" strokeLinecap="round" fill="none" />
                <path d="M262 318h42" stroke="#3b82f6" strokeWidth="5" strokeLinecap="round" />
                <circle cx="312" cy="318" r="4" fill="#60a5fa" />
                <rect x="366" y="238" width="20" height="34" rx="7" fill="#581c87" stroke="#a855f7" strokeWidth="2" />
                <circle cx="376" cy="255" r="5" fill="#a855f7" />
              </g>

              <rect x="257.5" y="52" width="5" height="376" fill="url(#seam)" rx="2.5" />

              <g fontFamily="Inter,system-ui,sans-serif" fontSize="13" fontWeight="600">
                <g>
                  <rect x="30" y="330" width="132" height="34" rx="17" fill="#fff" stroke="#bfdbfe" />
                  <circle cx="50" cy="347" r="6" fill="#2563eb" />
                  <text x="64" y="352" fill="#1e293b">Human touch</text>
                </g>
                <g>
                  <rect x="366" y="52" width="118" height="34" rx="17" fill="#fff" stroke="#e9d5ff" />
                  <circle cx="386" cy="69" r="6" fill="#7c3aed" />
                  <text x="400" y="74" fill="#1e293b">AI powered</text>
                </g>
              </g>
            </svg>
          </div>
        </div>
      </section>

      {/* ================= WHO ================= */}
      <section id="who" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="uppercase text-xs font-semibold tracking-widest text-blue-500 mb-3 block">{nl ? 'Wie we zijn' : 'Who we are'}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6 leading-tight">
              {nl ? 'AI for HR, met de mens als vertrekpunt' : 'AI for HR, starting from people'}
            </h2>
            <div className="space-y-4">
              {[
                nl
                  ? <><strong>Business eerst.</strong> We vertrekken van een concreet HR-probleem, niet van de technologie.</>
                  : <><strong>Business first.</strong> We start from a concrete HR problem, not from the technology.</>,
                nl
                  ? <><strong>Samen met je team.</strong> Je HR-medewerkers kennen het probleem het best — zij geven de oplossing mee vorm.</>
                  : <><strong>Together with your team.</strong> Your HR people know the problem best — they help shape the solution.</>,
                nl
                  ? <><strong>Adoptie als doel.</strong> Een oplossing die niet gebruikt wordt, is geen oplossing. Change zit in ons DNA.</>
                  : <><strong>Adoption as the goal.</strong> A solution nobody uses is no solution. Change is in our DNA.</>,
              ].map((point, i) => (
                <div key={`why-${i}`} className="flex gap-3 items-start">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-sm font-bold flex items-center justify-center">✓</span>
                  <p className="text-slate-600 leading-relaxed">{point}</p>
                </div>
              ))}
            </div>
          </div>
          <blockquote className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border-l-4 border-blue-500 shadow-sm">
            <p className="text-xl italic text-slate-800 leading-relaxed mb-4">
              {nl ? (
                <>&ldquo;Digitale transformatie verandert de manier van werken. AI verandert het werk.&rdquo;</>
              ) : (
                <>&ldquo;Digital transformation changes the way we work. AI changes the work itself.&rdquo;</>
              )}
            </p>
            <footer className="text-sm font-semibold text-blue-600">— Humanistix</footer>
          </blockquote>
        </div>
      </section>

      {/* ================= WHAT / PILLARS ================= */}
      <section id="what" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="uppercase text-xs font-semibold tracking-widest text-blue-500 mb-3 block">{nl ? 'Wat we doen' : 'What we do'}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 leading-tight">
              {nl ? 'Drie pijlers, één belofte: AI die werkt' : 'Three pillars, one promise: AI that works'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {nl ? 'Van concreet businessprobleem tot een team dat de nieuwe manier van werken omarmt.' : 'From a concrete business problem to a team that embraces the new way of working.'}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                num: '01',
                title: nl ? 'AI for HR' : 'AI for HR',
                body: nl
                  ? (<>Humanistix helpt HR-teams om AI in te zetten <strong>waar het vandaag al waarde oplevert</strong>. Geen hype, geen megaprojecten — wel werkende oplossingen voor de problemen die je HR-team elke dag voelt.</>)
                  : (<>Humanistix helps HR teams put AI to work <strong>where it creates value today</strong>. No hype, no mega-projects — just working solutions for the problems your HR team feels every day.</>),
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l2.2 4.8L19 10l-4.8 2.2L12 17l-2.2-4.8L5 10l4.8-2.2z" /><path d="M19 15l1 2.2L22.2 18 20 19l-1 2.2L18 19l-2.2-1 2.2-.8z" /></svg>
                ),
              },
              {
                num: '02',
                title: nl ? 'Gecontroleerd opbouwen' : 'Building step by step',
                body: nl
                  ? (<>Geen grote projecten, maar projecten met <strong>grote betrokkenheid van je HR-medewerkers</strong>. Zij leveren de input vanuit hun dagelijkse praktijk en geven onder onze begeleiding de oplossing op een echte agile manier vorm.</>)
                  : (<>No big projects, but projects with <strong>big involvement of your HR people</strong>. They bring the input from their daily practice and, under our guidance, shape the solution in a truly agile way.</>),
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="14" width="5" height="7" rx="1" /><rect x="10" y="9" width="5" height="12" rx="1" /><rect x="17" y="4" width="5" height="17" rx="1" /></svg>
                ),
              },
              {
                num: '03',
                title: nl ? 'Change & adoptie' : 'Change & adoption',
                body: nl
                  ? (<>AI verandert de manier van werken. Projecten slagen of falen <strong>niet door de technologie, maar door de mensen</strong>. Daarom begeleiden we je team van weerstand naar draagvlak — tot de nieuwe manier van werken vanzelfsprekend is.</>)
                  : (<>AI changes the way people work. Projects succeed or fail <strong>not because of the technology, but because of the people</strong>. That&apos;s why we guide your team from resistance to buy-in — until the new way of working feels natural.</>),
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.8 2.8 0 014 4L7.5 20.5 2 22l1.5-5.5z" /></svg>
                ),
              },
            ].map((c) => (
              <div key={c.num} className="relative bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <span className="absolute top-6 right-8 text-5xl font-black text-blue-100 leading-none select-none">{c.num}</span>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">{c.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{c.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= APPROACH ================= */}
      <section id="how" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="uppercase text-xs font-semibold tracking-widest text-blue-500 mb-3 block">{nl ? 'Onze aanpak' : 'Our approach'}</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">
              {nl ? 'Van idee tot oplossing, in vier stappen' : 'From idea to solution, in four steps'}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: '1', h: nl ? 'Quickscan' : 'Quickscan', p: nl ? 'Meet in 4 minuten hoe AI-ready je HR-team is, met benchmark tegen 245+ organisaties.' : "Measure your HR team's AI readiness in 4 minutes, benchmarked against 245+ organisations." },
              { n: '2', h: nl ? 'AI for HR Analyse' : 'AI for HR analysis', p: nl ? 'Samen met je HR-medewerkers bepalen we welk proces het meest schuurt — en het snelst waarde oplevert.' : 'Together with your HR people we identify the process that hurts most — and delivers value fastest.' },
              { n: '3', h: nl ? 'Agile bouwen' : 'Build agile', p: nl ? 'In korte sprints bouwen we de oplossing, met je team aan het stuur en ons als begeleider.' : 'In short sprints we build the solution, with your team at the wheel and us as guide.' },
              { n: '4', h: nl ? 'Adopteren' : 'Adopt', p: nl ? 'We begeleiden de verandering tot de nieuwe manier van werken écht ingeburgerd is.' : 'We guide the change until the new way of working truly sticks.' },
            ].map((s) => (
              <div key={s.n} className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border border-blue-100">
                <span className="inline-flex w-10 h-10 rounded-full bg-blue-500 text-white font-bold items-center justify-center mb-3 text-lg">{s.n}</span>
                <h4 className="text-lg font-bold text-slate-900 mb-2">{s.h}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= LATEST BLOG POSTS ================= */}
      {latestPosts.length > 0 && (
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="home-latest-posts">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="uppercase text-xs font-semibold tracking-widest text-blue-500 mb-3 block">Blog</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">
                {nl ? 'Laatste inzichten' : 'Latest insights'}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestPosts.map((post) => (
                <Link
                  key={post.id}
                  to="/blog"
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group flex flex-col"
                  data-testid="home-blog-card"
                >
                  {post.thumbnail && (
                    <img
                      src={post.thumbnail}
                      alt={post.title}
                      className="w-full h-36 object-cover rounded-xl mb-4"
                    />
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    {post.source === 'linkedin' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        <Linkedin className="h-3.5 w-3.5" />
                        {post.post_type === 'carousel' ? (nl ? 'LinkedIn carrousel' : 'LinkedIn carousel') : 'LinkedIn'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                        <Newspaper className="h-3.5 w-3.5" />
                        {nl ? 'Artikel' : 'Article'}
                      </span>
                    )}
                    <time className="text-xs text-slate-400">
                      {new Date(post.created_at).toLocaleDateString(nl ? 'nl-BE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </time>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  {(post.summary || post.content) && (
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
                      {post.summary || post.content}
                    </p>
                  )}
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-500">
                    {nl ? 'Lees meer' : 'Read more'}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                data-testid="home-all-posts-link"
              >
                {nl ? 'Bekijk alle posts' : 'View all posts'}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ================= CTA BAND ================= */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 leading-tight">
            {nl ? 'Hoe AI-ready is jouw HR-team?' : 'How AI-ready is your HR team?'}
          </h2>
          <p className="text-lg text-blue-50 mb-8 max-w-2xl mx-auto leading-relaxed">
            {nl
              ? "Doe de gratis quickscan van 4 minuten. Je krijgt meteen inzicht in kansen, risico's en concrete volgende stappen — vergeleken met 245+ HR-teams."
              : 'Take the free 4-minute quickscan. Get instant insight into opportunities, risks and concrete next steps — compared with 245+ HR teams.'}
          </p>
          <Link
            to="/quickscan/start"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 shadow-lg transition-all"
            data-testid="landing-cta-band"
          >
            {nl ? 'Start gratis quickscan' : 'Start free quickscan'}
            <span>›</span>
          </Link>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8 text-sm text-blue-50">
            <span>✓ {nl ? '100% gratis' : '100% free'}</span>
            <span>✓ {nl ? 'Realtime benchmark' : 'Real-time benchmark'}</span>
            <span>✓ {nl ? 'AI-inzichten' : 'AI-powered insights'}</span>
            <span>✓ {nl ? '4 minuten' : '4 minutes'}</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HumanistixLanding;
